import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Button from '../button/Button';

const styles = require('./Sheet.module.scss');

export interface SheetProps {
  cancelButtonText?: string;
  children: React.ReactNode;
  className?: string;
  context?: 'talent' | 'company' | 'neutral';
  continueButtonText?: string;
  isOpen?: boolean;
  onCancel?: () => void;
  onClose?: () => void;
  onContinue?: (data?: any) => void;
  showActions?: boolean;
  showCancelButton?: boolean;
  title: string | React.ReactNode;
  triggerId?: string;
}

const Sheet: React.FC<SheetProps> = ({
  cancelButtonText = 'Cancel',
  children,
  className,
  context = 'neutral',
  continueButtonText = 'Continue',
  isOpen: isOpenProp = true,
  onCancel,
  onClose,
  onContinue,
  showActions = true,
  showCancelButton = true,
  title,
  triggerId,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenProp);
  const [isClosing, setIsClosing] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const instanceId = useRef(`sheet-title-${Math.random().toString(36).slice(2, 9)}`);

  const handleClose = useCallback(() => {
    setIsClosing(true);
  }, []);

  const handleAnimationEnd = useCallback(() => {
    if (!isClosing) return;

    setIsOpen(false);
    setIsClosing(false);
    onClose?.();
  }, [isClosing, onClose]);

  useEffect(() => {
    setIsOpen(isOpenProp);
  }, [isOpenProp]);

  useEffect(() => {
    if (!triggerId) return;

    const trigger = document.getElementById(triggerId);
    if (!trigger) return;

    const handleOpen = () => setIsOpen(true);
    trigger.addEventListener('click', handleOpen);
    return () => trigger.removeEventListener('click', handleOpen);
  }, [triggerId]);

  useEffect(() => {
    if (!isOpen) return;

    previousFocusRef.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      sheetRef.current?.focus();
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [isOpen, handleClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  if (!isOpen) return null;

  const titleId = instanceId.current;

  return (
    <div
      className={classNames(styles.overlay, className, { [styles.closing]: isClosing })}
      onClick={handleBackdropClick}
    >
      <div
        ref={sheetRef}
        className={classNames(styles.sheet, { [styles.closing]: isClosing })}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onAnimationEnd={handleAnimationEnd}
      >
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>{title}</h2>
          <Button
            type="terceary"
            size="M"
            onClick={handleClose}
            iconLeft="x"
            context="neutral"
            aria-label="Close"
          />
        </div>

        <div className={styles.content}>{children}</div>

        {showActions && (
          <div className={styles.footer}>
            {showCancelButton && (
              <Button
                type="secondary"
                size="M"
                onClick={onCancel || handleClose}
                className={styles.actionButton}
              >
                {cancelButtonText}
              </Button>
            )}
            <Button
              type="primary"
              size="M"
              onClick={onContinue}
              className={styles.actionButton}
              context={context}
            >
              {continueButtonText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sheet;
